import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, Navigate, Route, Routes } from 'react-router-dom'

import { getCurrentUser, getOverview } from './api/admin'
import { Shell } from './components/layout/Shell'
import { Panel, ToastProvider, TotpProvider, ui } from './components/ui/Ui'
import { isAuthenticated, onSessionChange } from './lib/session'
import { useCopyEnabled, useCopyState, usePlatformSettings } from './lib/settings'
import Announcements from './screens/Announcements'
import Audit from './screens/Audit'
import DepositReversals from './screens/DepositReversals'
import Futures from './screens/Futures'
import Invites from './screens/Invites'
import Kyc from './screens/Kyc'
import Masters from './screens/Masters'
import Overview from './screens/Overview'
import Referrals from './screens/Referrals'
import Reports from './screens/Reports'
import Registry from './screens/Registry'
import Security from './screens/Security'
import Settings from './screens/Settings'
import SignIn from './screens/SignIn'
import Signals from './screens/Signals'
import Users from './screens/Users'
import Withdrawals from './screens/Withdrawals'

const TITLES: Record<string, string> = {
  '/': 'Обзор',
  '/withdrawals': 'Выводы',
  '/deposit-reversals': 'Сторнирования депозитов',
  '/futures': 'Фьючерсы',
  '/kyc': 'Верификация',
  '/users': 'Пользователи',
  '/masters': 'Мастера и стратегии',
  '/signals': 'Сигналы',
  '/referrals': 'Партнёрская программа',
  '/invites': 'Коды приглашений',
  '/networks': 'Сети и активы',
  '/announcements': 'Объявления',
  '/reports': 'Отчёты',
  '/settings': 'Настройки',
  '/audit': 'Аудит',
  '/security': 'Безопасность',
}

export function App(): JSX.Element {
  const [authenticated, setAuthenticated] = useState(isAuthenticated)
  useEffect(() => onSessionChange(setAuthenticated), [])

  const me = useQuery({ queryKey: ['me'], queryFn: getCurrentUser, enabled: authenticated, retry: false })
  const overview = useQuery({ queryKey: ['overview'], queryFn: getOverview, enabled: authenticated, refetchInterval: 20_000, retry: false })
  const copyEnabled = useCopyEnabled()

  if (!authenticated) {
    return <SignIn onSignedIn={() => setAuthenticated(true)} />
  }

  return (
    <ToastProvider>
      <TotpProvider>
        <Routes>
          {Object.keys(TITLES).map((path) => (
            <Route
              key={path}
              element={
                <Shell
                  operator={me.data?.user.email}
                  role={me.data?.user.role}
                  pendingWithdrawals={overview.data?.pendingWithdrawals ?? 0}
                  pendingKyc={overview.data?.pendingKyc ?? 0}
                  pendingDepositReversals={overview.data?.pendingDepositReversals ?? 0}
                  totpEnabled={me.data?.user.totpEnabled ?? true}
                  copyEnabled={copyEnabled}
                />
              }
            >
              <Route path={path} element={screenFor(path)} />
            </Route>
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TotpProvider>
    </ToastProvider>
  )
}

function screenFor(path: string): JSX.Element {
  switch (path) {
    case '/withdrawals':
      return <Withdrawals />
    case '/deposit-reversals':
      return <DepositReversals />
    case '/futures':
      return <Futures />
    case '/kyc':
      return <Kyc />
    case '/users':
      return <Users />
    case '/masters':
      return <MastersGate />
    case '/signals':
      return <Signals />
    case '/referrals':
      return <Referrals />
    case '/invites':
      return <Invites />
    case '/networks':
      return <Registry />
    case '/announcements':
      return <Announcements />
    case '/reports':
      return <Reports />
    case '/settings':
      return <Settings />
    case '/audit':
      return <Audit />
    case '/security':
      return <Security />
    default:
      return <Overview />
  }
}

/**
 * Copy trading is off since the 28 September pivot (`copy.enabled`). While it
 * is off the server answers every masters route with FEATURE_DISABLED, so the
 * address shows a note instead of a screen of errors; with the setting on it
 * opens as before.
 */
function MastersGate(): JSX.Element {
  const copy = useCopyState()
  const settings = usePlatformSettings()
  if (copy.enabled) return <Masters />
  if (!copy.settled) return <Panel><div style={{ padding: 20 }} className={ui.hint}>Загрузка…</div></Panel>
  return (
    <Panel>
      <div style={{ padding: 20 }} className={ui.stack}>
        <p className={ui.sectionTitle}>Копи-трейдинг выключен</p>
        <p style={{ margin: 0 }}>
          {settings.readable ? 'Настройка copy.enabled выключена' : 'С 28 сентября копи-трейдинг выключен настройкой copy.enabled (её видит администратор)'}: мастеров, рейтинга и подписок у пользователей нет, публичные адреса копирования отвечают «не найдено». Главный продукт теперь — сигналы по коду (раздел «Сигналы»).
        </p>
        <p className={ui.hint} style={{ margin: 0 }}>
          Пока настройка copy.enabled выключена, сервер не отдаёт данные мастеров и подписок; раздел откроется после её включения.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {settings.readable ? (
            <Link to="/settings?edit=copy.enabled" className={ui.link}>
              Включить в «Настройках»
            </Link>
          ) : (
            <span className={ui.hint}>Включает администратор в «Настройках».</span>
          )}
        </div>
      </div>
    </Panel>
  )
}
